import React, { useState } from 'react';
import {
  Form,
  useActionData,
  useLocation,
  useNavigate,
  useNavigation,
  useSubmit
} from 'react-router-dom';
import { TrashIcon } from '@heroicons/react/24/outline';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
  Legend
} from '@oysterjs/ui/fieldset';
import { Checkbox, CheckboxField } from '@oysterjs/ui/checkbox';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import { Text } from '@oysterjs/ui/text';
import { Divider } from '@oysterjs/ui/divider';
import { Button, PrimaryButton } from '@oysterjs/ui/button';
import { ValidationError } from '@oysterjs/core/graphql';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { states } from '@oysterjs/core/states';
import { CreateAgencyInput, CreateAgencyMutation, Role } from '../../../api/graphql';

type CreateAgencyPageProps = {
  /**
   * Path to navigate to when closing the slideout if there's no history.
   * Defaults to '/app/settings/subscription' for the AIS app.
   */
  defaultClosePath?: string;
  /**
   * Path prefix for navigating to the created agency on success.
   * The agency ID will be appended to this path.
   * Defaults to '/app/settings/subscription/agencies' for the AIS app.
   */
  successPathPrefix?: string;
};

export const CreateAgencyPage = ({
  defaultClosePath = '/app/settings/subscription',
  successPathPrefix = '/app/settings/subscription/agencies'
}: CreateAgencyPageProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigation = useNavigation();
  const submit = useSubmit();

  const action = useActionData() as ActionResult<CreateAgencyMutation> | undefined;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [useAgencyAddressAsProducer, setUseAgencyAddressAsProducer] = useState(true);

  const [form, setForm] = useState<CreateAgencyInput>({
    agency: {
      name: '',
      displayName: '',
      phone: '',
      logoUrl: '',
      wordmarkUrl: '',
      mailingAddress: {
        line1: '',
        line2: '',
        city: '',
        zone: '',
        postalCode: ''
      },
      configuration: {
        liabilityCOIConfiguration: {
          producer: {
            name: '',
            address: {
              line1: '',
              line2: '',
              city: '',
              zone: '',
              postalCode: ''
            }
          },
          contactName: '',
          contactEmail: '',
          contactPhone: '',
          authorizedRepName: '',
          renderAllCaps: false
        }
      }
    },
    users: [{ name: '', email: '', roles: [Role.User] }]
  });

  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  const onClose = () => {
    if (location.key === 'default') {
      navigate(defaultClosePath);
    } else {
      navigate(-1);
    }
  };

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (action?.applicationError) {
      setError(action.applicationError.message);
    }
  }, [action?.applicationError]);

  React.useEffect(() => {
    if (navigation.state === 'loading' && navigation.formAction === navigation.location?.pathname) {
      setLoading(false);
      if (
        !action?.validationErrors &&
        !action?.applicationError &&
        action?.data?.createAgency?.id
      ) {
        setError(null);
        navigate(`${successPathPrefix}/${action.data.createAgency.id}`, {
          replace: true
        });
      }
    }
  }, [
    navigation.state,
    navigation.formAction,
    navigation.location?.pathname,
    action?.validationErrors,
    action?.applicationError,
    action?.data?.createAgency?.id,
    successPathPrefix
  ]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Copy agency mailing address to producer address if checkbox is checked
    const submitForm: CreateAgencyInput = {
      ...form,
      agency: {
        ...form.agency,
        configuration: {
          ...form.agency.configuration,
          liabilityCOIConfiguration: {
            ...form.agency.configuration.liabilityCOIConfiguration,
            producer: {
              ...form.agency.configuration.liabilityCOIConfiguration.producer,
              address: useAgencyAddressAsProducer
                ? form.agency.mailingAddress
                : form.agency.configuration.liabilityCOIConfiguration.producer.address
            }
          }
        }
      }
    };

    submit(submitForm as unknown as Record<string, string>, {
      method: 'POST',
      encType: 'application/json'
    });
  };

  return (
    <MountableSlideout onClose={onClose} title="Create Subagency">
      <div>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
          Create a new subagency that will be managed under this organization. The subagency will
          share seats from this subscription.
        </p>
        <Form onSubmit={handleSubmit} className="space-y-8">
          {/* Agency Information */}
          <Fieldset>
            <Legend>Agency Information</Legend>
            <Text>
              Basic information about the subagency that will be used throughout the platform.
            </Text>
            <FieldGroup>
              <div className="flex flex-col gap-4">
                <Field>
                  <Label>Legal Name</Label>
                  <Description>Official registered name of the agency</Description>
                  <Input
                    value={form.agency.name}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      updateForm(['agency', 'name'], (prev) => ({
                        ...prev,
                        agency: { ...prev.agency, name: value }
                      }));
                    }}
                    placeholder="ACME Insurance Agency, LLC"
                    invalid={!!getValidationError(validationErrors, ['agency', 'name'])}
                  />
                  {getValidationError(validationErrors, ['agency', 'name']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['agency', 'name'])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Display Name</Label>
                  <Description>Name shown to clients and in the interface</Description>
                  <Input
                    value={form.agency.displayName}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      updateForm(['agency', 'displayName'], (prev) => ({
                        ...prev,
                        agency: { ...prev.agency, displayName: value }
                      }));
                    }}
                    placeholder="ACME Insurance"
                    invalid={!!getValidationError(validationErrors, ['agency', 'displayName'])}
                  />
                  {getValidationError(validationErrors, ['agency', 'displayName']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['agency', 'displayName'])}
                    </ErrorMessage>
                  )}
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field>
                  <Label>Phone</Label>
                  <Description>Main contact number for the agency</Description>
                  <Input
                    value={form.agency.phone}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      updateForm(['agency', 'phone'], (prev) => ({
                        ...prev,
                        agency: { ...prev.agency, phone: value }
                      }));
                    }}
                    type="tel"
                    placeholder="(555) 555-5555"
                    invalid={!!getValidationError(validationErrors, ['agency', 'phone'])}
                  />
                  {getValidationError(validationErrors, ['agency', 'phone']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['agency', 'phone'])}
                    </ErrorMessage>
                  )}
                </Field>
              </div>
            </FieldGroup>
          </Fieldset>

          <Divider />

          {/* Agency Mailing Address */}
          <Fieldset>
            <Legend>Agency Mailing Address</Legend>
            <Text>The mailing address for the subagency.</Text>
            <FieldGroup>
              <Field>
                <Label>Address Line 1</Label>
                <Input
                  value={form.agency.mailingAddress.line1}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    updateForm(['agency', 'mailingAddress', 'line1'], (prev) => ({
                      ...prev,
                      agency: {
                        ...prev.agency,
                        mailingAddress: {
                          ...prev.agency.mailingAddress,
                          line1: value
                        }
                      }
                    }));
                  }}
                  placeholder="123 Main Street"
                  invalid={
                    !!getValidationError(validationErrors, ['agency', 'mailingAddress', 'line1'])
                  }
                />
                {getValidationError(validationErrors, ['agency', 'mailingAddress', 'line1']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['agency', 'mailingAddress', 'line1'])}
                  </ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Address Line 2</Label>
                <Input
                  value={form.agency.mailingAddress.line2 || ''}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    updateForm(['agency', 'mailingAddress', 'line2'], (prev) => ({
                      ...prev,
                      agency: {
                        ...prev.agency,
                        mailingAddress: {
                          ...prev.agency.mailingAddress,
                          line2: value
                        }
                      }
                    }));
                  }}
                  placeholder="Suite 200"
                />
              </Field>
              <div className="grid grid-cols-3 gap-4">
                <Field>
                  <Label>City</Label>
                  <Input
                    value={form.agency.mailingAddress.city}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      updateForm(['agency', 'mailingAddress', 'city'], (prev) => ({
                        ...prev,
                        agency: {
                          ...prev.agency,
                          mailingAddress: {
                            ...prev.agency.mailingAddress,
                            city: value
                          }
                        }
                      }));
                    }}
                    placeholder="San Francisco"
                    invalid={
                      !!getValidationError(validationErrors, ['agency', 'mailingAddress', 'city'])
                    }
                  />
                  {getValidationError(validationErrors, ['agency', 'mailingAddress', 'city']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['agency', 'mailingAddress', 'city'])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>State</Label>
                  <Select
                    value={form.agency.mailingAddress.zone}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      updateForm(['agency', 'mailingAddress', 'zone'], (prev) => ({
                        ...prev,
                        agency: {
                          ...prev.agency,
                          mailingAddress: {
                            ...prev.agency.mailingAddress,
                            zone: value
                          }
                        }
                      }));
                    }}
                    invalid={
                      !!getValidationError(validationErrors, ['agency', 'mailingAddress', 'zone'])
                    }
                  >
                    <option value="">Select state</option>
                    {states.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.name}
                      </option>
                    ))}
                  </Select>
                  {getValidationError(validationErrors, ['agency', 'mailingAddress', 'zone']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['agency', 'mailingAddress', 'zone'])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Postal Code</Label>
                  <Input
                    value={form.agency.mailingAddress.postalCode}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      updateForm(['agency', 'mailingAddress', 'postalCode'], (prev) => ({
                        ...prev,
                        agency: {
                          ...prev.agency,
                          mailingAddress: {
                            ...prev.agency.mailingAddress,
                            postalCode: value
                          }
                        }
                      }));
                    }}
                    placeholder="94102"
                    invalid={
                      !!getValidationError(validationErrors, [
                        'agency',
                        'mailingAddress',
                        'postalCode'
                      ])
                    }
                  />
                  {getValidationError(validationErrors, [
                    'agency',
                    'mailingAddress',
                    'postalCode'
                  ]) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, [
                        'agency',
                        'mailingAddress',
                        'postalCode'
                      ])}
                    </ErrorMessage>
                  )}
                </Field>
              </div>
            </FieldGroup>
          </Fieldset>

          <Divider />

          {/* Producer Information */}
          <Fieldset>
            <Legend>Producer Information on ACORD forms</Legend>
            <Text>Producer details that will appear on ACORD forms and certificates.</Text>
            <FieldGroup>
              <Field>
                <Label>Producer Name</Label>
                <Input
                  value={form.agency.configuration.liabilityCOIConfiguration.producer.name}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    updateForm(
                      ['agency', 'configuration', 'liabilityCOIConfiguration', 'producer', 'name'],
                      (prev) => ({
                        ...prev,
                        agency: {
                          ...prev.agency,
                          configuration: {
                            ...prev.agency.configuration,
                            liabilityCOIConfiguration: {
                              ...prev.agency.configuration.liabilityCOIConfiguration,
                              producer: {
                                ...prev.agency.configuration.liabilityCOIConfiguration.producer,
                                name: value
                              }
                            }
                          }
                        }
                      })
                    );
                  }}
                  placeholder="ACME Insurance Agency"
                  invalid={
                    !!getValidationError(validationErrors, [
                      'agency',
                      'configuration',
                      'liabilityCOIConfiguration',
                      'producer',
                      'name'
                    ])
                  }
                />
                {getValidationError(validationErrors, [
                  'agency',
                  'configuration',
                  'liabilityCOIConfiguration',
                  'producer',
                  'name'
                ]) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, [
                      'agency',
                      'configuration',
                      'liabilityCOIConfiguration',
                      'producer',
                      'name'
                    ])}
                  </ErrorMessage>
                )}
              </Field>

              <CheckboxField>
                <Checkbox
                  checked={useAgencyAddressAsProducer}
                  onChange={setUseAgencyAddressAsProducer}
                  color="sky"
                />
                <Label>Use agency mailing address as producer address</Label>
              </CheckboxField>

              {!useAgencyAddressAsProducer && (
                <>
                  <Field>
                    <Label>Producer Address Line 1</Label>
                    <Input
                      value={
                        form.agency.configuration.liabilityCOIConfiguration.producer.address.line1
                      }
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(
                          [
                            'agency',
                            'configuration',
                            'liabilityCOIConfiguration',
                            'producer',
                            'address',
                            'line1'
                          ],
                          (prev) => ({
                            ...prev,
                            agency: {
                              ...prev.agency,
                              configuration: {
                                ...prev.agency.configuration,
                                liabilityCOIConfiguration: {
                                  ...prev.agency.configuration.liabilityCOIConfiguration,
                                  producer: {
                                    ...prev.agency.configuration.liabilityCOIConfiguration.producer,
                                    address: {
                                      ...prev.agency.configuration.liabilityCOIConfiguration
                                        .producer.address,
                                      line1: value
                                    }
                                  }
                                }
                              }
                            }
                          })
                        );
                      }}
                      placeholder="123 Main Street"
                      invalid={
                        !!getValidationError(validationErrors, [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'producer',
                          'address',
                          'line1'
                        ])
                      }
                    />
                    {getValidationError(validationErrors, [
                      'agency',
                      'configuration',
                      'liabilityCOIConfiguration',
                      'producer',
                      'address',
                      'line1'
                    ]) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'producer',
                          'address',
                          'line1'
                        ])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Producer Address Line 2</Label>
                    <Input
                      value={
                        form.agency.configuration.liabilityCOIConfiguration.producer.address
                          .line2 || ''
                      }
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(
                          [
                            'agency',
                            'configuration',
                            'liabilityCOIConfiguration',
                            'producer',
                            'address',
                            'line2'
                          ],
                          (prev) => ({
                            ...prev,
                            agency: {
                              ...prev.agency,
                              configuration: {
                                ...prev.agency.configuration,
                                liabilityCOIConfiguration: {
                                  ...prev.agency.configuration.liabilityCOIConfiguration,
                                  producer: {
                                    ...prev.agency.configuration.liabilityCOIConfiguration.producer,
                                    address: {
                                      ...prev.agency.configuration.liabilityCOIConfiguration
                                        .producer.address,
                                      line2: value
                                    }
                                  }
                                }
                              }
                            }
                          })
                        );
                      }}
                      placeholder="Suite 200"
                    />
                  </Field>
                  <div className="grid grid-cols-3 gap-4">
                    <Field>
                      <Label>City</Label>
                      <Input
                        value={
                          form.agency.configuration.liabilityCOIConfiguration.producer.address.city
                        }
                        onChange={(e) => {
                          const value = e.currentTarget.value;
                          updateForm(
                            [
                              'agency',
                              'configuration',
                              'liabilityCOIConfiguration',
                              'producer',
                              'address',
                              'city'
                            ],
                            (prev) => ({
                              ...prev,
                              agency: {
                                ...prev.agency,
                                configuration: {
                                  ...prev.agency.configuration,
                                  liabilityCOIConfiguration: {
                                    ...prev.agency.configuration.liabilityCOIConfiguration,
                                    producer: {
                                      ...prev.agency.configuration.liabilityCOIConfiguration
                                        .producer,
                                      address: {
                                        ...prev.agency.configuration.liabilityCOIConfiguration
                                          .producer.address,
                                        city: value
                                      }
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="San Francisco"
                        invalid={
                          !!getValidationError(validationErrors, [
                            'agency',
                            'configuration',
                            'liabilityCOIConfiguration',
                            'producer',
                            'address',
                            'city'
                          ])
                        }
                      />
                      {getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'producer',
                        'address',
                        'city'
                      ]) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, [
                            'agency',
                            'configuration',
                            'liabilityCOIConfiguration',
                            'producer',
                            'address',
                            'city'
                          ])}
                        </ErrorMessage>
                      )}
                    </Field>
                    <Field>
                      <Label>State</Label>
                      <Select
                        value={
                          form.agency.configuration.liabilityCOIConfiguration.producer.address.zone
                        }
                        onChange={(e) => {
                          const value = e.currentTarget.value;
                          updateForm(
                            [
                              'agency',
                              'configuration',
                              'liabilityCOIConfiguration',
                              'producer',
                              'address',
                              'zone'
                            ],
                            (prev) => ({
                              ...prev,
                              agency: {
                                ...prev.agency,
                                configuration: {
                                  ...prev.agency.configuration,
                                  liabilityCOIConfiguration: {
                                    ...prev.agency.configuration.liabilityCOIConfiguration,
                                    producer: {
                                      ...prev.agency.configuration.liabilityCOIConfiguration
                                        .producer,
                                      address: {
                                        ...prev.agency.configuration.liabilityCOIConfiguration
                                          .producer.address,
                                        zone: value
                                      }
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        invalid={
                          !!getValidationError(validationErrors, [
                            'agency',
                            'configuration',
                            'liabilityCOIConfiguration',
                            'producer',
                            'address',
                            'zone'
                          ])
                        }
                      >
                        <option value="">Select state</option>
                        {states.map((state) => (
                          <option key={state.value} value={state.value}>
                            {state.name}
                          </option>
                        ))}
                      </Select>
                      {getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'producer',
                        'address',
                        'zone'
                      ]) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, [
                            'agency',
                            'configuration',
                            'liabilityCOIConfiguration',
                            'producer',
                            'address',
                            'zone'
                          ])}
                        </ErrorMessage>
                      )}
                    </Field>
                    <Field>
                      <Label>Postal Code</Label>
                      <Input
                        value={
                          form.agency.configuration.liabilityCOIConfiguration.producer.address
                            .postalCode
                        }
                        onChange={(e) => {
                          const value = e.currentTarget.value;
                          updateForm(
                            [
                              'agency',
                              'configuration',
                              'liabilityCOIConfiguration',
                              'producer',
                              'address',
                              'postalCode'
                            ],
                            (prev) => ({
                              ...prev,
                              agency: {
                                ...prev.agency,
                                configuration: {
                                  ...prev.agency.configuration,
                                  liabilityCOIConfiguration: {
                                    ...prev.agency.configuration.liabilityCOIConfiguration,
                                    producer: {
                                      ...prev.agency.configuration.liabilityCOIConfiguration
                                        .producer,
                                      address: {
                                        ...prev.agency.configuration.liabilityCOIConfiguration
                                          .producer.address,
                                        postalCode: value
                                      }
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="94102"
                        invalid={
                          !!getValidationError(validationErrors, [
                            'agency',
                            'configuration',
                            'liabilityCOIConfiguration',
                            'producer',
                            'address',
                            'postalCode'
                          ])
                        }
                      />
                      {getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'producer',
                        'address',
                        'postalCode'
                      ]) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, [
                            'agency',
                            'configuration',
                            'liabilityCOIConfiguration',
                            'producer',
                            'address',
                            'postalCode'
                          ])}
                        </ErrorMessage>
                      )}
                    </Field>
                  </div>
                </>
              )}
            </FieldGroup>
          </Fieldset>

          <Divider />

          {/* Contact Information on ACORD forms */}
          <Fieldset>
            <Legend>Contact Information on ACORD forms</Legend>
            <Text>Contact details that will appear on ACORD forms and certificates.</Text>
            <FieldGroup>
              <div className="grid grid-cols-2 gap-4">
                <Field>
                  <Label>Contact Name</Label>
                  <Input
                    value={form.agency.configuration.liabilityCOIConfiguration.contactName}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      updateForm(
                        ['agency', 'configuration', 'liabilityCOIConfiguration', 'contactName'],
                        (prev) => ({
                          ...prev,
                          agency: {
                            ...prev.agency,
                            configuration: {
                              ...prev.agency.configuration,
                              liabilityCOIConfiguration: {
                                ...prev.agency.configuration.liabilityCOIConfiguration,
                                contactName: value
                              }
                            }
                          }
                        })
                      );
                    }}
                    placeholder="John Smith"
                    invalid={
                      !!getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'contactName'
                      ])
                    }
                  />
                  {getValidationError(validationErrors, [
                    'agency',
                    'configuration',
                    'liabilityCOIConfiguration',
                    'contactName'
                  ]) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'contactName'
                      ])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Authorized Representative</Label>
                  <Input
                    value={form.agency.configuration.liabilityCOIConfiguration.authorizedRepName}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      updateForm(
                        [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'authorizedRepName'
                        ],
                        (prev) => ({
                          ...prev,
                          agency: {
                            ...prev.agency,
                            configuration: {
                              ...prev.agency.configuration,
                              liabilityCOIConfiguration: {
                                ...prev.agency.configuration.liabilityCOIConfiguration,
                                authorizedRepName: value
                              }
                            }
                          }
                        })
                      );
                    }}
                    placeholder="Jane Doe"
                    invalid={
                      !!getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'authorizedRepName'
                      ])
                    }
                  />
                  {getValidationError(validationErrors, [
                    'agency',
                    'configuration',
                    'liabilityCOIConfiguration',
                    'authorizedRepName'
                  ]) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'authorizedRepName'
                      ])}
                    </ErrorMessage>
                  )}
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field>
                  <Label>Contact Email</Label>
                  <Input
                    value={form.agency.configuration.liabilityCOIConfiguration.contactEmail}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      updateForm(
                        ['agency', 'configuration', 'liabilityCOIConfiguration', 'contactEmail'],
                        (prev) => ({
                          ...prev,
                          agency: {
                            ...prev.agency,
                            configuration: {
                              ...prev.agency.configuration,
                              liabilityCOIConfiguration: {
                                ...prev.agency.configuration.liabilityCOIConfiguration,
                                contactEmail: value
                              }
                            }
                          }
                        })
                      );
                    }}
                    type="email"
                    placeholder="contact@acmeinsurance.com"
                    invalid={
                      !!getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'contactEmail'
                      ])
                    }
                  />
                  {getValidationError(validationErrors, [
                    'agency',
                    'configuration',
                    'liabilityCOIConfiguration',
                    'contactEmail'
                  ]) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'contactEmail'
                      ])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Contact Phone</Label>
                  <Input
                    value={form.agency.configuration.liabilityCOIConfiguration.contactPhone}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      updateForm(
                        ['agency', 'configuration', 'liabilityCOIConfiguration', 'contactPhone'],
                        (prev) => ({
                          ...prev,
                          agency: {
                            ...prev.agency,
                            configuration: {
                              ...prev.agency.configuration,
                              liabilityCOIConfiguration: {
                                ...prev.agency.configuration.liabilityCOIConfiguration,
                                contactPhone: value
                              }
                            }
                          }
                        })
                      );
                    }}
                    type="tel"
                    placeholder="(555) 555-5555"
                    invalid={
                      !!getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'contactPhone'
                      ])
                    }
                  />
                  {getValidationError(validationErrors, [
                    'agency',
                    'configuration',
                    'liabilityCOIConfiguration',
                    'contactPhone'
                  ]) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'contactPhone'
                      ])}
                    </ErrorMessage>
                  )}
                </Field>
              </div>
            </FieldGroup>
          </Fieldset>

          <Divider />

          {/* Agency Users */}
          <Fieldset>
            <Legend>Agency Users</Legend>
            <Text>
              Add the users who will have access to this subagency. Each user will consume one seat
              from your subscription. You can add more users after setup is complete.
            </Text>
            <FieldGroup>
              {Array.from({ length: form.users.length }).map((_, index) => (
                <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-4">
                  <Field>
                    <Label>Name</Label>
                    <Input
                      value={form.users[index]?.name || ''}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(['users', String(index), 'name'], (prev) => {
                          const newUsers = [...prev.users];
                          newUsers[index] = { ...newUsers[index], name: value };
                          return { ...prev, users: newUsers };
                        });
                      }}
                      placeholder="John Smith"
                      invalid={
                        !!getValidationError(validationErrors, ['users', String(index), 'name'])
                      }
                    />
                    {getValidationError(validationErrors, ['users', String(index), 'name']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['users', String(index), 'name'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Email</Label>
                    <Input
                      value={form.users[index]?.email || ''}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(['users', String(index), 'email'], (prev) => {
                          const newUsers = [...prev.users];
                          newUsers[index] = { ...newUsers[index], email: value };
                          return { ...prev, users: newUsers };
                        });
                      }}
                      type="email"
                      placeholder="john@acmeinsurance.com"
                      invalid={
                        !!getValidationError(validationErrors, ['users', String(index), 'email'])
                      }
                    />
                    {getValidationError(validationErrors, ['users', String(index), 'email']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['users', String(index), 'email'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <div className="mt-[calc(2rem_-_1px)] flex items-center h-10">
                    <button
                      type="button"
                      disabled={form.users.length === 1}
                      onClick={() => {
                        setForm((prev) => ({
                          ...prev,
                          users: prev.users.filter((_, i) => i !== index)
                        }));
                      }}
                      className={`py-2 rounded ${
                        form.users.length === 1
                          ? 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed'
                          : 'text-neutral-400 hover:text-neutral-700 dark:text-neutral-500 dark:hover:text-neutral-300'
                      }`}
                    >
                      <TrashIcon className="size-5" />
                    </button>
                  </div>
                </div>
              ))}
              <div className="mt-4">
                <Button
                  type="button"
                  color="light"
                  onClick={() => {
                    setForm((prev) => ({
                      ...prev,
                      users: [...prev.users, { name: '', email: '', roles: [Role.User] }]
                    }));
                  }}
                >
                  Add Another User
                </Button>
              </div>
            </FieldGroup>
          </Fieldset>

          {error && <ErrorMessage className="mt-4">{error}</ErrorMessage>}

          <div className="mt-6">
            <PrimaryButton type="submit" loading={loading || navigation.state === 'submitting'}>
              Create Agency
            </PrimaryButton>
          </div>
        </Form>
      </div>
    </MountableSlideout>
  );
};
